import styled from 'styled-components';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import {suffixTestId} from 'shared/web';

import {COMPARISON_HIGHLIGHT_ATTRIBUTE} from '../constants/COMPARISON_HIGHLIGHT_ATTRIBUTE';
import {COMPARISON_HOVERED_ROW_ATTRIBUTE} from '../constants/COMPARISON_HOVERED_ROW_ATTRIBUTE';
import {COMPARISON_ROW_ATTRIBUTE} from '../constants/COMPARISON_ROW_ATTRIBUTE';
import {useComparisonHover} from '../hooks/useComparisonHover';
import {ComparisonRowKey} from '../types/ComparisonRowKey';

interface ComparisonHoverCellProps {
  rowKey: ComparisonRowKey;
  children: ReactNode;
  isInLabelColumn?: boolean;
}

function highlightCellIfHovered(element: HTMLDivElement | null) {
  if (isNil(element)) {
    return;
  }

  const rowKey = element.getAttribute(COMPARISON_ROW_ATTRIBUTE);
  const surface = element.closest(`[${COMPARISON_HOVERED_ROW_ATTRIBUTE}]`);
  // A column mounted after hover adopts the current row. This relies on each cell keeping its rowKey.
  if (surface?.getAttribute(COMPARISON_HOVERED_ROW_ATTRIBUTE) === rowKey) {
    element.setAttribute(COMPARISON_HIGHLIGHT_ATTRIBUTE, 'true');
  }
}

export function ComparisonHoverCell(props: ComparisonHoverCellProps) {
  const {hasUnifiedSurfaces, testId} = useComparisonHover();
  if (!hasUnifiedSurfaces) {
    return <>{props.children}</>;
  }

  // The test ID repeats across vehicle columns because each cell shares the same row key.
  return (
    <Cell
      ref={highlightCellIfHovered}
      data-testid={suffixTestId(`comparison-hover-cell-${props.rowKey}`, {'data-testid': testId})}
      {...{[COMPARISON_ROW_ATTRIBUTE]: props.rowKey}}
      {...{[COMPARISON_HIGHLIGHT_ATTRIBUTE]: 'false'}}
      $isInLabelColumn={props.isInLabelColumn ?? false}
    >
      {props.children}
    </Cell>
  );
}

const Cell = styled.div<{$isInLabelColumn: boolean}>`
  position: relative;
  ${({$isInLabelColumn, theme}) =>
    $isInLabelColumn &&
    `
    margin-left: calc(-1 * ${theme.getSize(4)});
    width: calc(100% + ${theme.getSize(4)});
    box-sizing: border-box;
    padding-left: ${theme.getSize(4)};
  `}
  &::after {
    content: '';
    position: absolute;
    inset: ${({$isInLabelColumn, theme}) =>
      $isInLabelColumn ? `calc(-1 * ${theme.getSize(1)}) 0` : '0'};
    pointer-events: none;
    background-color: ${({theme}) => theme.colors.palettes.blue[60][100]};
    opacity: 0;
  }
  &[${COMPARISON_HIGHLIGHT_ATTRIBUTE}='true']::after {
    opacity: 0.08;
  }
`;
